import { useState, useCallback } from 'react';

const DEVICE_NAME_KEY = 'workmanager_device_name';

/**
 * Get a device identifier: user-set name or derived from browser.
 */
export function getDeviceName() {
  try {
    const stored = localStorage.getItem(DEVICE_NAME_KEY);
    if (stored && String(stored).trim()) return String(stored).trim();
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const platform = typeof navigator !== 'undefined' ? navigator.platform : '';
    return [platform || 'unknown', ua.substring(0, 80)].filter(Boolean).join(' | ');
  } catch {
    return 'unknown';
  }
}

/**
 * Set device name (e.g. "Shop Kiosk 1"). Stored in localStorage.
 */
export function setDeviceName(name) {
  try {
    if (name != null && String(name).trim()) {
      localStorage.setItem(DEVICE_NAME_KEY, String(name).trim());
    } else {
      localStorage.removeItem(DEVICE_NAME_KEY);
    }
  } catch (e) {
    console.warn('setDeviceName failed', e);
  }
}

/** Error code for permission denied (user blocked or dismissed) */
export const LOCATION_PERMISSION_DENIED = 'Location permission denied';

/** Instructions shown when user must re-enable in browser settings */
export const LOCATION_ENABLE_INSTRUCTIONS =
  'Mac: System Settings → Privacy → Location Services. iPhone: Settings → Privacy → Location → Safari/Chrome. Android: Settings → Location ON, then Chrome → lock icon → Permissions → Location Allow. Refresh the page.';

/**
 * Request location permission by calling getCurrentPosition.
 * This triggers the browser's native permission prompt (if not yet granted/denied).
 * Returns { latitude, longitude } or throws.
 */
export function requestLocationPermission(options = {}) {
  return getCurrentPosition(options);
}

/**
 * Get current position. Returns { latitude, longitude } or throws.
 * Required for punch; call before submitting clock in/out.
 */
export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude
        });
      },
      (err) => {
        if (err.code === 1) reject(new Error(LOCATION_PERMISSION_DENIED));
        else if (err.code === 2) reject(new Error('Location unavailable'));
        else if (err.code === 3) reject(new Error('Location request timed out'));
        else reject(new Error(err.message || 'Failed to get location'));
      },
      { timeout: 15000, maximumAge: 60000, enableHighAccuracy: false, ...options }
    );
  });
}

/**
 * Hook: current location + device for punch. Use getPunchPayload() before submit.
 * requestLocationPermission() triggers the browser's location prompt.
 */
export function usePunchLocation() {
  const [locationError, setLocationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requestingPermission, setRequestingPermission] = useState(false);

  const requestLocationPermission = useCallback(async () => {
    setRequestingPermission(true);
    setLocationError(null);
    try {
      await getCurrentPosition();
      setLocationError(null);
      return true;
    } catch (err) {
      const msg = err.message || 'Location is required. Please enable location and try again.';
      setLocationError(err.message === LOCATION_PERMISSION_DENIED
        ? `${msg} ${LOCATION_ENABLE_INSTRUCTIONS}`
        : msg);
      return false;
    } finally {
      setRequestingPermission(false);
    }
  }, []);

  const getPunchPayload = useCallback(async (forClockOut = false) => {
    setLoading(true);
    setLocationError(null);
    try {
      const position = await getCurrentPosition();
      const device_name = getDeviceName();
      const device_info = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      if (forClockOut) {
        return {
          latitude_out: position.latitude,
          longitude_out: position.longitude,
          device_name,
          device_info
        };
      }
      return {
        latitude_in: position.latitude,
        longitude_in: position.longitude,
        device_name,
        device_info
      };
    } catch (err) {
      const base = err.message || 'Location is required. Please enable location and try again.';
      const message = err.message === LOCATION_PERMISSION_DENIED
        ? `${base} ${LOCATION_ENABLE_INSTRUCTIONS}`
        : base;
      setLocationError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    getPunchPayload,
    locationError,
    setLocationError,
    loading,
    getDeviceName,
    requestLocationPermission,
    requestingPermission
  };
}
